import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

/**
 * The component vocabulary the prototypes established.
 *
 * UI-04: every colour, size and spacing value here reads a `--mk-*` token. There
 * are no literals, which is what keeps the deferred editorial theme a token swap
 * rather than a sixty-three-screen sweep.
 */

/**
 * The title of a screen, and what it is about.
 *
 * **Compact, deliberately.** It used to be a 27px title with a sentence under it and 20px below
 * — around 90px of every screen, every time, saying something a person knew after their first
 * visit. On a list that is the difference between seeing nine rows and seeing six, all day.
 *
 * The description is not deleted: several carry live context rather than prose — which portal
 * and currency, the day a figure was taken, where a retailer is — and that is worth keeping. It
 * sits BESIDE the title now instead of under it, so it costs a line only when it wraps.
 */
export function PageHeader(
  { title, sub, actions, aside }: { title: string; sub?: ReactNode; actions?: ReactNode; aside?: ReactNode },
) {
  return (
    <div className="mk-ph">
      <h1>{title}</h1>
      {/* Sits on the title's own line — a price beside a name, not under it. */}
      {aside ? <div className="mk-ph-aside">{aside}</div> : null}
      {sub ? <p>{sub}</p> : null}
      <span className="mk-spacer" />
      {actions}
    </div>
  );
}

/**
 * A form that interrupts, rather than a panel that pushes the table down.
 *
 * Built on the native `<dialog>` deliberately. Focus trapping, restoring focus
 * to whatever opened it, Escape, inertness of the page behind and the backdrop
 * itself are all things a hand-rolled overlay gets subtly wrong; here the
 * platform does them. `showModal()` is called from an effect because React owns
 * the element, and `cancel` is intercepted so an Escape press goes through the
 * same `onClose` as the buttons — otherwise the dialog would vanish while the
 * state that renders it still said it was open.
 *
 * `footer` sits outside the scrolling body, so the primary action stays reachable
 * on a long form without hunting for it.
 */
export function Modal(
  { title, onClose, children, footer, wide }:
  {
    title: string; onClose: () => void; children: ReactNode;
    footer?: ReactNode; wide?: boolean;
  },
) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (el && !el.open) el.showModal();
  }, []);

  return (
    <dialog
      ref={ref}
      className={`mk-modal ${wide ? 'mk-modal-w' : ''}`}
      onCancel={(e) => { e.preventDefault(); onClose(); }}
      // A click landing on the dialog itself rather than its contents is the
      // backdrop, which is the conventional way out of a modal.
      onClick={(e) => { if (e.target === ref.current) onClose(); }}
    >
      <header className="mk-mtitle">
        <h2>{title}</h2>
        <span className="mk-spacer" />
        <button type="button" className="mk-mclose" onClick={onClose} aria-label="Close">×</button>
      </header>
      <div className="mk-mbody">{children}</div>
      {footer ? <footer className="mk-mfoot">{footer}</footer> : null}
    </dialog>
  );
}

/**
 * Tabs, for a form too tall to take in at once.
 *
 * The role editor was a single column of forty-two permissions, thirteen
 * screens and a portal list — nobody could see its structure, only scroll
 * through it. Sections that are genuinely independent become tabs; the count on
 * each is what tells you where to go, so the tab strip doubles as the summary
 * the long form never had.
 */
export function Tabs(
  { tabs, active, onChange }:
  {
    tabs: ReadonlyArray<{ id: string; label: string; badge?: string; disabled?: boolean }>;
    active: string;
    onChange: (id: string) => void;
  },
) {
  return (
    <div className="mk-tabs" role="tablist">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={active === t.id}
          disabled={t.disabled}
          className={`mk-tab ${active === t.id ? 'on' : ''}`}
          onClick={() => onChange(t.id)}
        >
          {t.label}
          {t.badge ? <span className="mk-tab-n">{t.badge}</span> : null}
        </button>
      ))}
    </div>
  );
}

export function Panel(
  { title, actions, children, pad }: { title?: string; actions?: ReactNode; children: ReactNode; pad?: boolean },
) {
  return (
    <section className="mk-panel">
      {title || actions ? (
        <header className="mk-ptitle">
          {title ? <h2>{title}</h2> : null}
          <span className="mk-spacer" />
          {actions}
        </header>
      ) : null}
      {pad ? <div className="mk-pbody">{children}</div> : children}
    </section>
  );
}

/** Status families, carried by treatment as well as hue — UI-05. */
export type Tone = 'us' | 'them' | 'done' | 'stop' | 'run' | 'lapse';
const GLYPH: Record<Tone, string> = {
  us: '◗', them: '◎', done: '●', stop: '■', run: '▸', lapse: '◌',
};

export function Chip(
  { tone, children, title }:
  {
    tone: Tone; children: ReactNode;
    /**
     * Optional hover text, for a state whose CONSEQUENCE does not fit in the chip.
     *
     * A chip names a state in a word; sometimes the word is not enough on first meeting — a
     * product's `Draft` means no retailer can see it and no order can name a variant of it,
     * which is the kind of thing a person needs once and then never again. This is where that
     * sentence goes instead of a full-width banner on every visit.
     *
     * Hover only, so never the only place a rule is stated: anything a person MUST know to act
     * belongs in the flow that stops them, not here.
     */
    title?: string;
  },
) {
  return (
    <span className={`mk-chip mk-chip-${tone}`} title={title}>
      <span className="mk-gl" aria-hidden>{GLYPH[tone]}</span>
      {children}
    </span>
  );
}

export function Button(
  { children, onClick, tone = 'solid', size = 'md', disabled, type = 'button', title }:
  {
    children: ReactNode; onClick?: () => void;
    /** `danger` is `ghost` recoloured with the same `mk-st-stop-*` tokens a
     *  refusal chip or alert already uses — for an action with no undo, not
     *  a new colour of its own. */
    tone?: 'solid' | 'ghost' | 'danger';
    size?: 'sm' | 'md'; disabled?: boolean; type?: 'button' | 'submit'; title?: string;
  },
) {
  const toneClass = tone === 'danger' ? 'mk-btn-g mk-btn-danger' : tone === 'ghost' ? 'mk-btn-g' : '';
  return (
    <button
      type={type}
      className={`mk-btn ${toneClass} ${size === 'sm' ? 'mk-btn-s' : ''}`}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {children}
    </button>
  );
}

export function Field(
  { label, hint, error, children, className }:
    {
      label: string; hint?: ReactNode; children: ReactNode; className?: string;
      /**
       * What is wrong with THIS field, said beside it.
       *
       * Forms here had nowhere to put that, so a refusal could only appear as one banner at
       * the top of a dialog — "Validation failed" — leaving the reader to work out which of
       * six inputs it meant. A reason belongs next to the thing it is about.
       *
       * It replaces the hint while it is showing rather than stacking under it: the hint says
       * what is wanted and the error says why what is there will not do, so showing both puts
       * two competing instructions under one input.
       */
      error?: string | null;
    },
) {
  const cls = ['mk-field', className, error ? 'mk-field-bad' : ''].filter(Boolean).join(' ');
  return (
    <label className={cls}>
      <span className="mk-lbl">{label}</span>
      {children}
      {error
        ? <span className="mk-field-err" role="alert">{error}</span>
        : hint ? <span className="mk-hint">{hint}</span> : null}
    </label>
  );
}

export interface Column<T> {
  /** Almost always a string; a ReactNode header (a Select All checkbox) is the exception. */
  header: ReactNode;
  /** Right-align numeric columns so figures line up — UI-10. */
  numeric?: boolean;
  /** Centre a column whose content is a fixed-size control or badge — a
   *  checkbox, a thumbnail, a status chip — rather than text meant to be
   *  scanned from its left edge. */
  center?: boolean;
  render: (row: T) => ReactNode;
  width?: string;
}

/**
 * Cells are keyed by POSITION, not by header text.
 *
 * `key={c.header}` looked reasonable and was wrong: a screen may carry several
 * columns with no header at all — a bulk-select checkbox, a thumbnail, a row
 * action — and every one of them keyed as the empty string. React warned on
 * `ProductList` (two blank headers) and `ProductDetail` (four), and a duplicate
 * key lets React drop or reuse the wrong cell as rows change.
 *
 * Position is safe here because a screen's column list is static: it is written
 * out once per table and never reordered or filtered at runtime.
 */
export function Table<T>(
  { columns, rows, empty, dense = true, rowKey, rowClassName, className, filter, filterLabel }:
  {
    columns: Column<T>[]; rows: T[]; empty?: ReactNode; dense?: boolean; rowKey: (row: T) => string;
    /**
     * A filter box above the table, matching on whatever this returns for each row.
     *
     * **Here rather than on each screen**, because every list wants one and thirty-three
     * screens writing their own would be thirty-three slightly different boxes — some
     * case-sensitive, some debounced, some losing the text on a refetch. A list long enough to
     * scroll is a list someone needs to search.
     *
     * Client-side, over the rows already held, and that is the right trade for these: the
     * screens pass a whole portal's worth of rows, so there is nothing a server round trip
     * would find that is not already here. The one list that outgrows that is the audit log,
     * which has a server-side search of its own and does not pass this.
     */
    filter?: (row: T) => string;
    /** What the box is for, when "Filter" is not specific enough. */
    filterLabel?: string;
    /** e.g. `'on'` for a selected row — the existing `.mk-t tr.on` treatment
     *  (brand-wash background, a left accent bar), not a new one. */
    rowClassName?: (row: T) => string | undefined;
    /** A modifier scoped to one screen's own table (e.g. `table.mk-t.mk-foo`
     *  in that screen's own CSS) — every other `Table` in the app is
     *  unaffected, since nothing here changes without this being passed. */
    className?: string;
  },
) {
  const [needle, setNeedle] = useState('');
  /*
   * A table that names no widths and no layout of its own gets equal columns, so the headings sit at an
   * even rhythm instead of each column taking whatever its content happens to need. A table that sets
   * widths (or a `className` with its own layout) keeps them. The floor is the columns' own share and
   * nothing more: a flat 560px made a two-column table scroll sideways on a 375px screen with every
   * value already fitting.
   */
  const evenColumns = !className && !columns.some((c) => c.width);
  const term = needle.trim().toLowerCase();
  /*
   * Every word must appear, in any order and any field: "harbour closed" finds the closed
   * Harbour account whichever column each word came from. One substring over a joined string
   * would demand the words be adjacent and in the order the columns happen to be written.
   */
  const words = term === '' ? [] : term.split(/\s+/);
  const shown = filter && words.length > 0
    ? rows.filter((r) => {
      const hay = filter(r).toLowerCase();
      return words.every((w) => hay.includes(w));
    })
    : rows;

  // The box stays when a filter matches nothing — it is the thing you need to correct.
  if (rows.length === 0 && empty) return <>{empty}</>;
  return (
    <>
      {filter ? (
        <div className="mk-tfilter">
          <input
            className="mk-fld"
            type="search"
            value={needle}
            aria-label={filterLabel ?? 'Filter this list'}
            placeholder={filterLabel ?? 'Filter…'}
            onChange={(e) => setNeedle(e.target.value)}
          />
          <span className="mk-hint">
            {words.length > 0 ? `${shown.length} of ${rows.length}` : `${rows.length}`}
          </span>
        </div>
      ) : null}
      <div className="mk-twrap">
      <table
        className={`mk-t ${dense ? 'mk-dense' : ''} ${evenColumns ? 'mk-equal' : ''} ${className ?? ''}`}
        style={evenColumns ? { minWidth: columns.length * 104 } : undefined}
      >
        <thead>
          <tr>
            {columns.map((c, i) => (
              // `width` was declared on Column and never applied — a dead API that
              // read as available. `1%` is the idiom for "as narrow as the content
              // allows", which is how a wide column gets the remaining space.
              <th
                key={`${i}`}
                style={c.width ? { width: c.width } : undefined}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shown.length === 0 ? (
            // A filter that matches nothing leaves an empty table and no explanation. Say it.
            <tr><td colSpan={columns.length} className="mk-dim" style={{ padding: '14px 10px' }}>
              Nothing matches “{needle.trim()}”.
            </td></tr>
          ) : null}
          {shown.map((r) => (
            <tr key={rowKey(r)} className={rowClassName?.(r)}>
              {columns.map((c, i) => (
                // `width` on the header alone was the same dead-API bug one
                // more time: auto table layout takes its width hint from
                // EITHER cell in a column, but leaving it off every body
                // cell let a wide value in one row still stretch the whole
                // column past what its header hinted, row after row.
                <td
                  key={`${i}`}
                  className={c.numeric ? 'mk-num' : c.center ? 'mk-centered' : undefined}
                  style={c.width ? { width: c.width } : undefined}
                >
                  {c.render(r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </>
  );
}

export function Tiles({ items }: { items: Array<{ k: string; v: ReactNode; s?: ReactNode }> }) {
  return (
    <div className="mk-tiles">
      {items.map((t) => (
        <div className="mk-tile" key={t.k}>
          <div className="mk-tk">{t.k}</div>
          <div className="mk-tv">{t.v}</div>
          {t.s ? <div className="mk-ts">{t.s}</div> : null}
        </div>
      ))}
    </div>
  );
}

export function Rail({ items }: { items: Array<[string, ReactNode]> }) {
  return (
    <div className="mk-rail">
      {items.map(([k, v]) => (
        <div key={k}>
          <span className="mk-rk">{k}</span>
          <span className="mk-rv">{v}</span>
        </div>
      ))}
    </div>
  );
}

export function Alert(
  { tone, children, actions }: { tone: Tone; children: ReactNode; actions?: ReactNode },
) {
  return (
    <div className={`mk-alert mk-alert-${tone}`}>
      <span aria-hidden>{GLYPH[tone]}</span>
      <div>{children}</div>
      {actions ? <><span className="mk-spacer" />{actions}</> : null}
    </div>
  );
}

export function Empty(
  { glyph = '◌', title, body, action }:
  { glyph?: string; title: string; body?: ReactNode; action?: ReactNode },
) {
  return (
    <div className="mk-empty">
      <div className="mk-g" aria-hidden>{glyph}</div>
      <h3>{title}</h3>
      {body ? <p>{body}</p> : null}
      {action}
    </div>
  );
}

export function Note({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <div className="mk-bd" style={style}>{children}</div>;
}

/**
 * Money, for reading.
 *
 * Takes the decimal string the API sent and never converts it to a number.
 * Grouping is applied to the integer part by hand: `Number(...).toLocaleString()`
 * would round at 2^53 and silently lose the fourth decimal place (NF-04).
 */
export function Money(
  { amount, currency, suffix, trimZeros = true, plain = false }: {
    amount: string | null | undefined; currency?: string | undefined; suffix?: boolean;
    /** `100.00` reads as `100`; `100.50` keeps its two places. On by default so every
     *  figure in the portal reads the same way; pass `false` where the cents
     *  must show (a statement reconciled to the cent). Display only. */
    trimZeros?: boolean;
    /** Show every stored place that is not a trailing zero: `970.5000` reads `970.5`, `970.2500` reads
     *  `970.25`. For a price column, where `.50` would suggest a precision the figure does not have. */
    plain?: boolean;
  },
) {
  // `== null` catches undefined as well as null, deliberately. A display
  // component must not be able to take the application down: a field the API
  // stopped sending arrived here as undefined, `.startsWith` threw, and with no
  // error boundary above it React unmounted the whole tree to a blank page.
  // An em-dash is visibly wrong and debuggable; a blank page is neither.
  if (amount == null) return <span className="mk-dim">—</span>;
  const negative = amount.startsWith('-');
  const [whole = '0', frac = '0000'] = amount.replace('-', '').split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  // Four places are stored; two are shown. The other two are not lost, they are
  // simply not a thing a person reads on a list screen.
  const shown = plain ? frac.replace(/0+$/, '') : frac.slice(0, 2).padEnd(2, '0');
  const value = plain
    ? <>{negative ? '−' : ''}{grouped}{shown ? `.${shown}` : ''}</>
    : trimZeros && shown === '00'
    ? <>{negative ? '−' : ''}{grouped}</>
    : <>{negative ? '−' : ''}{grouped}.{shown}</>;
  const cur = currency ? <span className="mk-cur">{currency}</span> : null;
  // `suffix` is the catalog card's own convention (amount, then currency) —
  // every other reading in the app keeps the prefix order (`GBP 420.00`), so
  // this is opt-in per call site, not a global change.
  return (
    <span className="mk-money">
      {suffix ? <>{value}{cur}</> : <>{cur}{value}</>}
    </span>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return <div className="mk-loading" role="status">{label}…</div>;
}
